/**
 * @file RbtResumeGuide.jsx
 * @description Standalone SEO/career-content page at /rbt-resume-guide, the answer to "rbt resume".
 *   Written for an RBT or soon-to-be RBT on a phone, often with no ABA experience:
 *   short answer, a copyable resume layout, the RBT line (active / in progress), no-experience
 *   translation cards, before and after bullets, what RBT postings ask for (from 10 job pages read),
 *   skills in BACB exam-outline words, settings/ages/hours/area, mistakes, FAQ (with FAQPage
 *   JSON-LD from the same keys) and one quiet path into an ABAcases profile.
 *   Educational content only: no named employers/clients, no case or provider counts, no legal
 *   advice. Requirements are linked to the BACB. Every card stacks to one column on phones.
 * @module components/RbtResumeGuide
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  MapPinIcon, ClockIcon, UsersIcon, AwardIcon, CopyIcon, CheckIcon, ExternalLinkIcon,
  BellIcon, ClipboardIcon, AlertTriangleIcon, UserCheckIcon, ShieldIcon,
  CalendarIcon, HeartIcon, FileTextIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import GuideReviewStamp from "./GuideReviewStamp";
import { FaqDetails } from "./Disclosure";
import { GuideAtAGlance, GuideYourPath, GuideYourPathChips } from "./GuideShared";
import { setSignupIntent } from "../lib/signupIntent";
import { useFaqJsonLd, useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta } from "../lib/analytics";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

const BACB_RBT_URL = "https://www.bacb.com/rbt/";
const BACB_HANDBOOK_URL = "https://www.bacb.com/rbt-handbook/";
const BACB_TCO_URL = "https://www.bacb.com/wp-content/rbt-outline-3rdEd/";

const range = (n) => Array.from({ length: n }, (_, i) => i + 1);
const FAQ_ITEMS = range(5).map((n) => ({ qKey: `rbtResumeGuide.faq${n}Q`, aKey: `rbtResumeGuide.faq${n}A` }));

const SCAN_CARDS = [
  { Icon: AwardIcon, n: 1 }, { Icon: UserCheckIcon, n: 2 }, { Icon: CalendarIcon, n: 3 }, { Icon: MapPinIcon, n: 4 },
  { Icon: ShieldIcon, n: 5 }, { Icon: HeartIcon, n: 6 }, { Icon: ClipboardIcon, n: 7 },
];
const FIT_CARDS = [
  { Icon: UsersIcon, key: "fitAges" }, { Icon: ClockIcon, key: "fitHours" }, { Icon: MapPinIcon, key: "fitArea" },
];
const SETTING_KEYS = ["setHome", "setSchool", "setClinic", "setCommunity", "setRemote"];

/** Copy text to the clipboard; falls back to a hidden textarea on older phones. Returns true on success. */
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (e2) {
      return false;
    }
  }
}

/** Shared inline styles for this page; phones get tighter padding and one-column grids. */
function pageStyles(isMobile) {
  return {
    sectionPad: isMobile ? "40px 18px" : "60px 48px",
    maxW: 900,
    alt: { background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` },
    h2Style: { fontSize: isMobile ? 23 : 29, fontWeight: 900, color: NAVY, margin: "0 0 12px", letterSpacing: "-0.02em", lineHeight: 1.2 },
    introStyle: { fontSize: 15.5, color: TEXT_MID, lineHeight: 1.65, margin: "0 0 18px" },
    gridStyle: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 12 },
    cardStyle: { background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "16px 18px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" },
  };
}

/** A titled page section (optionally on the alternate background). Module-level so state changes never remount it. */
function Section({ id, tone, testId, title, intro, children, sty }) {
  return (
    <section id={id} data-testid={testId} style={{ ...(tone === "alt" ? sty.alt : null), padding: sty.sectionPad }}>
      <div style={{ maxWidth: sty.maxW, margin: "0 auto" }}>
        <h2 style={sty.h2Style}>{title}</h2>
        {intro && <p style={sty.introStyle}>{intro}</p>}
        {children}
      </div>
    </section>
  );
}

function IconTile({ Icon, color = INDIGO, bg = "#ede9fe" }) {
  return (
    <div style={{ width: 40, height: 40, borderRadius: 10, background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon size={18} color={color} />
    </div>
  );
}

/** Icon + title + body card; stacks one per row on phones. */
function InfoCard({ Icon, title, body, iconColor, iconBg, sty }) {
  return (
    <div style={{ ...sty.cardStyle, display: "flex", gap: 14, alignItems: "flex-start" }}>
      <IconTile Icon={Icon} color={iconColor} bg={iconBg} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15.5, fontWeight: 800, color: NAVY, marginBottom: 4, lineHeight: 1.3 }}>{title}</div>
        <div style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.6 }}>{body}</div>
      </div>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "9px 14px", background: "#fff", border: `1.5px solid ${BORDER}`, borderRadius: 999, fontSize: 14, fontWeight: 700, color: NAVY, lineHeight: 1.3 }}>{children}</span>
  );
}

/** Crawlable internal link (real <a href> so Googlebot follows it; preventDefault keeps SPA nav). */
function SeoLink({ to, onNavigate, children }) {
  return (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", color: INDIGO, fontWeight: 700 }}>{children}</a>
  );
}

function BacbLink({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: INDIGO, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span>{children}</span> <ExternalLinkIcon size={12} color={INDIGO} />
    </a>
  );
}

export default function RbtResumeGuide({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  usePageSeo({
    title: "RBT Resume Guide | Template, Examples, No Experience",
    description: "How to write an RBT resume: what to include, a layout you can copy, before and after examples, and what to write with no ABA experience.",
    canonical: "/rbt-resume-guide",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-rbt-resume-guide", [
    { name: "Home", url: "/" },
    { name: "RBT Resume Guide", url: "/rbt-resume-guide" },
  ]);
  // One source of truth: the same i18n keys feed the visible FAQ rows and the FAQPage JSON-LD.
  useFaqJsonLd("faq-jsonld-rbt-resume-guide", FAQ_ITEMS.map((f) => ({ q: t(f.qKey), a: t(f.aKey) })));

  const goJoin = () => {
    track(A.RESOURCE_PAGE_CTA_CLICK, {
      supabaseMeta: { page: "/rbt-resume-guide/" },
      ga4Params: ctaMeta({
        pagePath: "/rbt-resume-guide/",
        pageType: "resource",
        ctaLabel: "Join ABA case alerts",
        targetPath: "/auth",
        userLanguage: i18n.language,
        audience: "resource",
      }),
    });
    setSignupIntent("alerts");
    onNavigate("auth");
  };

  const onCopy = async () => {
    if (await copyText(t("rbtResumeGuide.layoutText"))) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const sty = pageStyles(isMobile);
  const { sectionPad, maxW, h2Style, introStyle, gridStyle, cardStyle } = sty;
  const sectionProps = { isMobile, sty };


  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "20px 18px 36px" : "32px 48px 60px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          <Breadcrumbs items={[
            { label: t('rbtResumeGuide.breadcrumbHome'), onClick: () => onNavigate("home") },
            { label: t('rbtResumeGuide.breadcrumbCurrent') },
          ]} />
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t('rbtResumeGuide.badgeLabel')}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>
          <h1 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t('rbtResumeGuide.heroTitle')}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 22px" }}>
            {t('rbtResumeGuide.heroSubtitle')}
          </p>
          <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 48, padding: isMobile ? "13px 24px" : "15px 30px", background: `linear-gradient(135deg, ${INDIGO} 0%, #6366F1 100%)`, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
            <BellIcon size={16} color="#fff" /> {t('rbtResumeGuide.ctaJoinFree')}
          </button>
        </div>
      </header>
      <GuideYourPathChips current="rbt-resume-guide" onNavigate={onNavigate} isMobile={isMobile} />

      {/* ── Short answer ─────────────────────────────────────── */}
      <GuideAtAGlance
        isMobile={isMobile}
        title={t('rbtResumeGuide.shortTitle')}
        items={range(5).map((n) => t(`rbtResumeGuide.shortItem${n}`))}
        confirmPre={t('rbtResumeGuide.shortConfirm')}
        confirmHref={BACB_RBT_URL}
        confirmLabel="bacb.com/rbt"
      />

      {/* ── Copyable layout ──────────────────────────────────── */}
      <Section {...sectionProps} id="layout" testId="resume-layout" title={t('rbtResumeGuide.layoutTitle')} intro={t('rbtResumeGuide.layoutIntro')}>
        <div style={{ ...cardStyle, borderLeft: `4px solid ${INDIGO}`, padding: isMobile ? "16px 16px" : "20px 24px" }}>
          <pre data-testid="resume-layout-text" style={{ margin: 0, fontFamily: T.fontBody, fontSize: 14.5, lineHeight: 1.65, color: NAVY, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{t('rbtResumeGuide.layoutText')}</pre>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px 16px", marginTop: 14 }}>
          <button data-testid="copy-layout" onClick={onCopy} aria-live="polite" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 46, padding: "11px 20px", background: "#fff", color: INDIGO, border: `1.5px solid ${INDIGO}`, borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer" }}>
            {copied ? <CheckIcon size={16} color={INDIGO} /> : <CopyIcon size={16} color={INDIGO} />}
            {copied ? t('rbtResumeGuide.layoutCopied') : t('rbtResumeGuide.layoutCopy')}
          </button>
          <span style={{ fontSize: 14.5, color: TEXT_MID, fontWeight: 600 }}>{t('rbtResumeGuide.layoutTip')}</span>
        </div>
      </Section>

      {/* ── Your RBT line ────────────────────────────────────── */}
      <Section {...sectionProps} id="rbt-line" tone="alt" testId="rbt-line" title={t('rbtResumeGuide.credTitle')} intro={t('rbtResumeGuide.credIntro')}>
        <div style={{ ...gridStyle, marginBottom: 18 }}>
          {range(4).map((n) => (
            <div key={n} style={cardStyle}>
              <div style={{ fontSize: 12, fontWeight: 800, color: INDIGO, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6 }}>{t(`rbtResumeGuide.cred${n}Title`)}</div>
              <div style={{ fontSize: 15.5, fontWeight: 700, color: NAVY, lineHeight: 1.45, marginBottom: 8 }}>{t(`rbtResumeGuide.cred${n}Line`)}</div>
              <div style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.55 }}>{t(`rbtResumeGuide.cred${n}Note`)}</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.7, margin: "0 0 10px" }}>{t('rbtResumeGuide.credSteps')}</p>
        <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.7, margin: 0 }}>
          {t('rbtResumeGuide.credStepsPre')}{" "}
          <BacbLink href={BACB_HANDBOOK_URL}>{t('rbtResumeGuide.credStepsLink')}</BacbLink>
          {t('rbtResumeGuide.credStepsMid')}{" "}
          <SeoLink onNavigate={onNavigate} to="how-to-become-an-rbt">{t('rbtResumeGuide.credStepsLink2')}</SeoLink>
          {t('rbtResumeGuide.credStepsPost')}
        </p>
      </Section>

      {/* ── No ABA experience yet ────────────────────────────── */}
      <section data-testid="no-experience" style={{ background: "#f5f3ff", borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2Style}>{t('rbtResumeGuide.noExpTitle')}</h2>
          <p style={introStyle}>{t('rbtResumeGuide.noExpBody')}</p>
          <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderLeft: `4px solid ${INDIGO}`, borderRadius: 10, padding: "16px 18px", marginBottom: 28 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: INDIGO, letterSpacing: "0.02em", marginBottom: 6 }}>{t('rbtResumeGuide.noExpExampleLabel')}</div>
            <div style={{ fontSize: 15, color: NAVY, lineHeight: 1.6, fontStyle: "italic" }}>{t('rbtResumeGuide.noExpExample')}</div>
          </div>
          <h3 style={{ fontSize: isMobile ? 18 : 21, fontWeight: 900, color: NAVY, margin: "0 0 14px", letterSpacing: "-0.015em", lineHeight: 1.25 }}>{t('rbtResumeGuide.noExpMapTitle')}</h3>
          <div style={gridStyle}>
            {range(6).map((n) => (
              <div key={n} style={cardStyle}>
                <div style={{ fontSize: 15.5, fontWeight: 800, color: NAVY, marginBottom: 6, lineHeight: 1.3 }}>{t(`rbtResumeGuide.noExpMap${n}Title`)}</div>
                <div style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.6 }}>{t(`rbtResumeGuide.noExpMap${n}Body`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Before and after ─────────────────────────────────── */}
      <Section {...sectionProps} id="before-after" testId="before-after" title={t('rbtResumeGuide.baTitle')} intro={t('rbtResumeGuide.baIntro')}>
        <div style={gridStyle}>
          {range(7).map((n) => (
            <div key={n} data-testid="before-after-card" style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", background: "#fef2f2", borderBottom: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: "#b91c1c", letterSpacing: "0.06em", marginBottom: 4 }}>{t('rbtResumeGuide.baBefore')}</div>
                <div style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.5 }}>{t(`rbtResumeGuide.ba${n}Before`)}</div>
              </div>
              <div style={{ padding: "14px 18px", background: "#fff" }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO, letterSpacing: "0.06em", marginBottom: 4 }}>{t('rbtResumeGuide.baAfter')}</div>
                <div style={{ fontSize: 15, color: NAVY, fontWeight: 600, lineHeight: 1.5 }}>{t(`rbtResumeGuide.ba${n}After`)}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── What agencies scan for ───────────────────────────── */}
      <Section {...sectionProps} id="agencies-scan" tone="alt" testId="agencies-scan" title={t('rbtResumeGuide.scanTitle')} intro={t('rbtResumeGuide.scanIntro')}>
        <div style={gridStyle}>
          {SCAN_CARDS.map((c) => (
            <InfoCard sty={sty} key={c.n} Icon={c.Icon} title={t(`rbtResumeGuide.scan${c.n}Title`)} body={t(`rbtResumeGuide.scan${c.n}Body`)} />
          ))}
        </div>
      </Section>

      {/* ── Skills to list ───────────────────────────────────── */}
      <Section {...sectionProps} id="skills" testId="skills" title={t('rbtResumeGuide.skillsTitle')} intro={t('rbtResumeGuide.skillsIntro')}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          {range(14).map((n) => <Chip key={n}>{t(`rbtResumeGuide.skill${n}`)}</Chip>)}
        </div>
        <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.6, margin: 0 }}>
          {t('rbtResumeGuide.skillsSourcePre')}{" "}
          <BacbLink href={BACB_TCO_URL}>{t('rbtResumeGuide.skillsSourceLink')}</BacbLink>
          {t('rbtResumeGuide.skillsSourcePost')}
        </p>
      </Section>

      {/* ── Settings, ages, hours, area ──────────────────────── */}
      <Section {...sectionProps} id="fit" tone="alt" testId="fit" title={t('rbtResumeGuide.fitTitle')} intro={t('rbtResumeGuide.fitIntro')}>
        <div style={{ fontSize: 12, fontWeight: 800, color: INDIGO, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 8 }}>{t('rbtResumeGuide.fitSettingsLabel')}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
          {SETTING_KEYS.map((k) => <Chip key={k}>{t(`rbtResumeGuide.${k}`)}</Chip>)}
        </div>
        <div style={gridStyle}>
          {FIT_CARDS.map((c) => (
            <InfoCard sty={sty} key={c.key} Icon={c.Icon} title={t(`rbtResumeGuide.${c.key}Title`)} body={t(`rbtResumeGuide.${c.key}Body`)} />
          ))}
        </div>
      </Section>

      {/* ── Common mistakes ──────────────────────────────────── */}
      <Section {...sectionProps} id="mistakes" testId="mistakes" title={t('rbtResumeGuide.mistakesTitle')}>
        <div style={gridStyle}>
          {[1, 2, 4].map((n) => (
            <InfoCard sty={sty} key={n} Icon={AlertTriangleIcon} iconColor={T.yellow} iconBg={T.yellowLight} title={t(`rbtResumeGuide.mistake${n}Title`)} body={t(`rbtResumeGuide.mistake${n}Body`)} />
          ))}
        </div>
      </Section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section data-testid="resume-faq" style={{ ...sty.alt, padding: sectionPad }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ ...h2Style, margin: "0 0 18px" }}>{t('rbtResumeGuide.faqTitle')}</h2>
          {FAQ_ITEMS.map((f, i) => <FaqDetails key={f.qKey} q={t(f.qKey)} a={t(f.aKey)} defaultOpen={i === 0} isMobile={isMobile} />)}
        </div>
      </section>

      {/* ── One quiet path into ABAcases ─────────────────────── */}
      <section data-testid="resume-helps" style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto", display: "flex", gap: 14, alignItems: "flex-start" }}>
          {!isMobile && <IconTile Icon={FileTextIcon} />}
          <div>
            <h2 style={h2Style}>{t('rbtResumeGuide.helpsTitle')}</h2>
            <p style={{ ...introStyle, marginBottom: 12 }}>
              {t('rbtResumeGuide.helpsPre')}{" "}
              <SeoLink onNavigate={onNavigate} to="aba-provider-waitlist">{t('rbtResumeGuide.helpsLink')}</SeoLink>{" "}
              {t('rbtResumeGuide.helpsPost')}
            </p>
            <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.7, margin: 0 }}>
              {t('rbtResumeGuide.helpsJobsPre')}{" "}
              <SeoLink onNavigate={onNavigate} to="rbt-jobs-miami-fl">{t('rbtResumeGuide.helpsJobsMiami')}</SeoLink>{" "}
              {t('rbtResumeGuide.helpsJobsConnector')}{" "}
              <SeoLink onNavigate={onNavigate} to="rbt-jobs-charlotte-nc">{t('rbtResumeGuide.helpsJobsCharlotte')}</SeoLink>
              {t('rbtResumeGuide.helpsJobsPost')}{" "}
              <SeoLink onNavigate={onNavigate} to="rbt-interview-questions">{t('rbtResumeGuide.helpsInterviewLink')}</SeoLink>
              {t('rbtResumeGuide.helpsInterviewPost')}
            </p>
          </div>
        </div>
      </section>

      {/* ── Source note ──────────────────────────────────────── */}
      <div style={{ padding: isMobile ? "4px 18px 24px" : "4px 48px 28px", textAlign: "center", background: "#fff" }}>
        <p style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6, maxWidth: 640, margin: "0 auto" }}>{t('rbtResumeGuide.sourceNote')}</p>
      </div>

      <GuideYourPath current="rbt-resume-guide" onNavigate={onNavigate} isMobile={isMobile} />
      <GuideReviewStamp slug="rbt-resume-guide" />
      <PublicFooter />
    </div>
  );
}
